import { useState, useEffect, useMemo } from "react";
import { MdTrendingUp, MdFileDownload, MdPeople, MdTask } from "react-icons/md";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";

const exportToCsv = (filename, headers, rows) => {
  const encode = (val) => {
    if (val === null || val === undefined) return "";
    const str = String(val).replace(/"/g, '""');
    return `"${str}"`;
  };
  const allRows = [headers, ...rows];
  const csv = allRows.map((r) => r.map(encode).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: "8px 13px",
        boxShadow: "0 4px 14px rgba(0,0,0,.1)",
        fontSize: "0.8rem",
      }}
    >
      {label && <p style={{ fontWeight: 700, marginBottom: 4 }}>{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill, fontWeight: 600 }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

const roleLabel = (role) => {
  if (role === "super_admin") return "Super Admin";
  if (role === "admin") return "Admin";
  if (role === "accounts") return "Accounts";
  if (role === "hr") return "HR";
  return "Staff";
};

const StaffPerformanceReport = () => {
  const { isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [tRes, uRes] = await Promise.all([
          api.get("/tasks/admin/all", { params: { reportsView: 1 } }),
          api.get("/users/list"),
        ]);
        const allTasks = tRes.data.data || [];
        const allUsers = uRes.data.data || [];
        setTasks(allTasks);
        setUsers(allUsers);
        if (!selectedUserId && allUsers.length) {
          setSelectedUserId(allUsers[0]._id);
        }
      } finally {
        setLoading(false);
      }
    };
    if (isAdmin) load();
  }, [isAdmin, selectedUserId]);

  const allUserOptions = useMemo(
    () => users.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [users]
  );

  const selectedUser = useMemo(
    () => allUserOptions.find((u) => u._id === selectedUserId) || null,
    [allUserOptions, selectedUserId]
  );

  const userTasks = useMemo(() => {
    if (!selectedUser) return [];
    return tasks.filter(
      (t) =>
        t.createdBy?._id === selectedUser._id ||
        t.assignedTo?._id === selectedUser._id
    );
  }, [tasks, selectedUser]);

  const metrics = useMemo(() => {
    if (!selectedUser) {
      return {
        created: 0,
        assigned: 0,
        done: 0,
        pending: 0,
        total: 0,
        completionRate: 0,
        firstCreatedAt: null,
        lastCompletedAt: null,
        averageCompletionHours: 0,
      };
    }
    const created = userTasks.filter(
      (t) => t.createdBy?._id === selectedUser._id
    );
    // Assigned tasks are ONLY those created by someone else and assigned to this user
    const assigned = userTasks.filter(
      (t) =>
        t.assignedTo?._id === selectedUser._id &&
        t.createdBy?._id !== selectedUser._id
    );
    const done = userTasks.filter((t) => t.status === "done");
    const pending = userTasks.filter((t) => t.status !== "done");
    const total = userTasks.length;
    const completionRate =
      total > 0 ? Math.round((done.length / total) * 100) : 0;
    const createdAtDates = userTasks
      .map((t) => (t.createdAt ? new Date(t.createdAt) : null))
      .filter(Boolean);
    const completedDates = done
      .map((t) => (t.updatedAt ? new Date(t.updatedAt) : null))
      .filter(Boolean);
    const firstCreatedAt =
      createdAtDates.length > 0
        ? new Date(Math.min(...createdAtDates.map((d) => d.getTime())))
        : null;
    const lastCompletedAt =
      completedDates.length > 0
        ? new Date(Math.max(...completedDates.map((d) => d.getTime())))
        : null;

    // Average completion time in hours (from createdAt to updatedAt) for completed tasks
    const completionDurations = done
      .map((t) => {
        if (!t.createdAt || !t.updatedAt) return null;
        const start = new Date(t.createdAt).getTime();
        const end = new Date(t.updatedAt).getTime();
        if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
          return null;
        }
        const hours = (end - start) / (1000 * 60 * 60);
        return hours;
      })
      .filter((h) => h != null);

    const averageCompletionHours =
      completionDurations.length > 0
        ? Number(
            (
              completionDurations.reduce((sum, h) => sum + h, 0) /
              completionDurations.length
            ).toFixed(1),
          )
        : 0;
    return {
      created: created.length,
      assigned: assigned.length,
      done: done.length,
      pending: pending.length,
      total,
      completionRate,
      firstCreatedAt,
        lastCompletedAt,
        averageCompletionHours,
    };
  }, [userTasks, selectedUser]);

  const performanceData = useMemo(() => {
    if (!selectedUser) return [];
    return [
      {
        id: selectedUser._id,
        name: selectedUser.name.split(" ")[0],
        created: metrics.created,
        assigned: metrics.assigned,
        done: metrics.done,
        completionRate: metrics.completionRate,
      },
    ];
  }, [selectedUser, metrics]);

  if (!isAdmin) {
    return (
      <main className="page">
        <div className="card card-body">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <MdTrendingUp size={26} color="var(--primary)" />
            <div>
              <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Staff performance report
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "var(--text-muted)",
                  marginTop: 4,
                }}
              >
                This report is available for admin users only.
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="page">
        <div className="loading">
          <div className="spinner" />
          <span className="loading-text">Loading staff performance...</span>
        </div>
      </main>
    );
  }

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <MdTrendingUp style={{ marginRight: 6 }} />
            Staff performance
          </h1>
          <p className="page-subtitle">
            Real-time view of tasks created, assigned and completed for the
            selected user.
          </p>
        </div>
        {selectedUser && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() =>
              exportToCsv(
                "staff-performance.csv",
                [
                  "User",
                  "Created",
                  "Assigned",
                  "Done",
                  "Pending",
                  "Completion %",
                  "Avg completion (hrs)",
                ],
                [
                  [
                    selectedUser.name,
                    metrics.created,
                    metrics.assigned,
                    metrics.done,
                    metrics.pending,
                    metrics.completionRate,
                    metrics.averageCompletionHours,
                  ],
                ]
              )
            }
          >
            <MdFileDownload size={15} style={{ marginRight: 4 }} />
            Export Excel
          </button>
        )}
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div
          className="card-body"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ minWidth: 220 }}>
            <label className="form-label">Select user</label>
            <select
              className="form-control"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              {allUserOptions.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.name} ({roleLabel(u.role)})
                </option>
              ))}
            </select>
          </div>

          {selectedUser && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                flex: 1,
                justifyContent: "flex-end",
              }}
            >
              <div className="stat-card" style={{ minWidth: 140 }}>
                <div
                  style={{
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    color: "var(--text-muted)",
                    marginBottom: 4,
                  }}
                >
                  Tasks Assigned
                </div>
                <div className="stat-value">{metrics.assigned}</div>
              </div>
              <div className="stat-card" style={{ minWidth: 140 }}>
                <div
                  style={{
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    color: "var(--text-muted)",
                    marginBottom: 4,
                  }}
                >
                  Tasks Completed
                </div>
                <div className="stat-value">{metrics.done}</div>
              </div>
              <div className="stat-card" style={{ minWidth: 140 }}>
                <div
                  style={{
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    color: "var(--text-muted)",
                    marginBottom: 4,
                  }}
                >
                  Tasks Pending
                </div>
                <div className="stat-value">{metrics.pending}</div>
              </div>
              <div className="stat-card" style={{ minWidth: 140 }}>
                <div
                  style={{
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    color: "var(--text-muted)",
                    marginBottom: 4,
                  }}
                >
                  Completion Rate
                </div>
                <div className="stat-value">{metrics.completionRate}%</div>
              </div>
              <div className="stat-card" style={{ minWidth: 160 }}>
                <div
                  style={{
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    color: "var(--text-muted)",
                    marginBottom: 4,
                  }}
                >
                  Avg Completion Time
                </div>
                <div className="stat-value">
                  {metrics.averageCompletionHours
                    ? `${metrics.averageCompletionHours} hrs`
                    : "—"}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {(!selectedUser || performanceData.length === 0) ? (
        <div className="card card-body">
          <div className="empty-state">
            <MdPeople size={40} />
            <h3>No data</h3>
            <p>Select a user that has related tasks to see performance.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="card card-body" style={{ marginBottom: 20 }}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={performanceData}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--border)"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize: "0.72rem" }} />
                <Bar
                  dataKey="created"
                  stackId="a"
                  name="Created"
                  fill="#6366f1"
                />
                <Bar
                  dataKey="assigned"
                  stackId="a"
                  name="Assigned"
                  fill="#f59e0b"
                />
                <Bar dataKey="done" name="Done" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card">
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid var(--border)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>
                Tasks for {selectedUser?.name}
              </span>
              <span
                style={{
                  fontSize: "0.78rem",
                  color: "var(--text-muted)",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <MdTask size={14} />
                {metrics.total} task{metrics.total === 1 ? "" : "s"}
              </span>
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Created At</th>
                    <th>Assigned At</th>
                  </tr>
                </thead>
                <tbody>
                  {userTasks.map((t) => (
                    <tr key={t._id}>
                      <td>{t.title}</td>
                      <td className="text-muted">
                        {t.status || "—"}
                      </td>
                      <td className="text-muted">
                        {t.priority || "—"}
                      </td>
                      <td className="text-muted">
                        {t.createdAt
                          ? new Date(t.createdAt).toLocaleString()
                          : "—"}
                      </td>
                      <td className="text-muted">
                        {t.assignedAt
                          ? new Date(t.assignedAt).toLocaleString()
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </main>
  );
};

export default StaffPerformanceReport;

